import { useFlyOutProductsBasedOnSelectedCloud } from '../../utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { CloudAndProductFilter } from '@labrav/react-components'
import {
  actionTypeWrapper,
  cloudTypes,
  PartnerType,
  useCurrentCloudProvider,
  useUserType,
} from '@labrav/common'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { useFlag } from '@labrav/flags'
import { SelectChangeEvent } from '@mui/material'

import { useDispatch } from '../../../store'
import {
  CloudMarketplace,
  FlyoutFunction,
} from '../../../modules/productsListing/types'
import {
  getFlyoutProductsListBasedOnFunction,
  setCloudMarketplaceProductListing,
  updateProductIdOnFilters,
} from '../../../modules/productListingFilter/action'
import { setSelectedProductOnListing } from '../../../modules/productsListing/action'

export interface CloudAndProductFiltersProps {
  functionType: FlyoutFunction
  children?: React.ReactNode
  externalBaseURL?: string
  search?: string
}

export const CloudAndProductFilters: React.FC<CloudAndProductFiltersProps> = ({
  functionType,
  children,
  externalBaseURL,
  search,
}: CloudAndProductFiltersProps) => {
  const dispatch = useDispatch()

  const { pathname } = useLocation()
  const cloudMarketplace = useCurrentCloudProvider()
  const { selectedProduct: productId, products } =
    useFlyOutProductsBasedOnSelectedCloud()
  const isAdmin = useUserType() === PartnerType.Admin

  const baseURL = useMemo(() => {
    if (externalBaseURL) {
      return externalBaseURL
        ?.split('product-listing')[0]
        ?.concat('product-listing')
    } else {
      return '/flyout'
    }
  }, [externalBaseURL])

  const handleProductChange = useCallback(
    async (event: SelectChangeEvent) => {
      const product = event.target.value as string
      const actualProductId = product === 'allProducts' ? '' : product
      await dispatch(
        updateProductIdOnFilters(
          actualProductId,
          cloudMarketplace,
          functionType
        )
      )
    },
    [baseURL, search, functionType, pathname, cloudMarketplace, dispatch]
  )

  useEffect(() => {
    if (!isEmpty(products) && productId === undefined) {
      dispatch(
        updateProductIdOnFilters(
          products?.length === 1 ? products[0].productId : '',
          cloudMarketplace,
          functionType
        )
      )
    }
  }, [products, productId, cloudMarketplace, functionType, dispatch])

  const productsForFilters = getFlyoutProductsListBasedOnFunction(
    products,
    functionType,
    cloudMarketplace
  )

  const _cloudTypes = cloudTypes.map(cloud =>
    (cloud.name === 'AZURE' || cloud.name === 'GCP') &&
    functionType === FlyoutFunction.PRIVATE_OFFERS &&
    !isAdmin
      ? { ...cloud, disabled: false }
      : cloud
  )

  const handleCloudChange = useCallback(
    async (event: SelectChangeEvent) => {
      await dispatch(
        actionTypeWrapper(
          cloudMarketplace,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(
        setCloudMarketplaceProductListing(
          event.target.value as CloudMarketplace
        )
      )
    },
    [cloudMarketplace, dispatch]
  )

  return (
    <div>
      <CloudAndProductFilter
        cloudTypes={_cloudTypes}
        selectedCloud={cloudMarketplace}
        products={productsForFilters}
        selectedProductId={isEmpty(productId) ? 'allProducts' : productId || ''}
        handleCloudChange={handleCloudChange}
        handleProductChange={handleProductChange}
      >
        {children}
      </CloudAndProductFilter>
    </div>
  )
}

/** @deprecated Use CloudAndProductFilters */
export const Filters = CloudAndProductFilters
export type FiltersProps = CloudAndProductFiltersProps
